import axios from 'axios';
import createSignedRequest from './createSignedRequest';
import { toggleAlertMessage } from '../action.js';

const { apiURL } = require('./apiURL');

const BASE = '/my/intelligent-imports';

export const Types = {
  IMPORT_SET_CURRENT_JOB: 'IMPORT_SET_CURRENT_JOB',
  IMPORT_SET_JOBS: 'IMPORT_SET_JOBS',
  IMPORT_SET_STAGING: 'IMPORT_SET_STAGING',
  IMPORT_SET_FILTERED_ROWS: 'IMPORT_SET_FILTERED_ROWS',
  IMPORT_SET_MATCHES: 'IMPORT_SET_MATCHES',
  IMPORT_SET_ENRICHMENT: 'IMPORT_SET_ENRICHMENT',
  IMPORT_SET_POLLING: 'IMPORT_SET_POLLING',
  IMPORT_SET_LOADING: 'IMPORT_SET_LOADING',
  IMPORT_SET_ERROR: 'IMPORT_SET_ERROR',
  IMPORT_CLEAR_JOB: 'IMPORT_CLEAR_JOB',
  IMPORT_SET_ORG_DATE_FORMAT: 'IMPORT_SET_ORG_DATE_FORMAT',
  IMPORT_SET_AI_QUOTA: 'IMPORT_SET_AI_QUOTA',
  IMPORT_SET_QUALITY_SCORE: 'IMPORT_SET_QUALITY_SCORE',
  IMPORT_SET_LEDGER_MAPPINGS: 'IMPORT_SET_LEDGER_MAPPINGS',
};

const setCurrentJob = (payload) => ({ type: Types.IMPORT_SET_CURRENT_JOB, payload });
const setJobs = (payload) => ({ type: Types.IMPORT_SET_JOBS, payload });
const setStaging = (payload) => ({ type: Types.IMPORT_SET_STAGING, payload });
const setFilteredRows = (payload) => ({ type: Types.IMPORT_SET_FILTERED_ROWS, payload });
const setMatches = (payload) => ({ type: Types.IMPORT_SET_MATCHES, payload });
const setEnrichment = (payload) => ({ type: Types.IMPORT_SET_ENRICHMENT, payload });
const setPolling = (payload) => ({ type: Types.IMPORT_SET_POLLING, payload });
const setLoading = (section, value) => ({ type: Types.IMPORT_SET_LOADING, payload: { section, value } });
const setError = (payload) => ({ type: Types.IMPORT_SET_ERROR, payload });
const clearJob = () => ({ type: Types.IMPORT_CLEAR_JOB });
const setOrgDateFormat = (payload) => ({ type: Types.IMPORT_SET_ORG_DATE_FORMAT, payload });
const setAiQuota = (payload) => ({ type: Types.IMPORT_SET_AI_QUOTA, payload });
const setQualityScore = (payload) => ({ type: Types.IMPORT_SET_QUALITY_SCORE, payload });
const setLedgerMappings = (payload) => ({ type: Types.IMPORT_SET_LEDGER_MAPPINGS, payload });

const orgHeader = (state) => ({ Organisation: String(state.currentOrganisation) });

const snakeToCamel = (s) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

const normalizeKeys = (obj) => {
  if (Array.isArray(obj)) return obj.map(normalizeKeys);
  if (obj && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.entries(obj).reduce((acc, [key, val]) => {
      acc[snakeToCamel(key)] = normalizeKeys(val);
      return acc;
    }, {});
  }
  return obj;
};

/**
 * Creates an import job and uploads the file to S3 via presigned URL.
 * @param {File} file
 * @param {string} [sourceHint] - BANK | LEDGER | SUPPLIER | DOCUMENT | UNSPECIFIED
 * @returns {Function} thunk
 */
export const initiateImport = (file, sourceHint) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('initiate', true));
  dispatch(setError(null));

  try {
    const body = {
      filename: file.name,
      contentType: file.type,
      ...(sourceHint && sourceHint !== 'UNSPECIFIED' && { sourceHint }),
    };

    const response = await axios(
      await createSignedRequest('POST', apiURL + BASE + '/initiate', JSON.stringify(body), orgHeader(state)),
    );

    const data = response.data;
    const importId = data.import_id || data.importId || data.id;
    const uploadUrl = data.upload_url || data.uploadUrl || data.url;

    if (!uploadUrl) {
      throw new Error('No upload URL returned from initiate endpoint');
    }

    await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });

    dispatch(setCurrentJob({ id: importId, filename: file.name, pipelineStatus: 'CREATED' }));
    dispatch(setLoading('initiate', false));
    return importId;
  } catch (e) {
    dispatch(setError(e.message || 'Failed to initiate import'));
    dispatch(setLoading('initiate', false));
    dispatch(toggleAlertMessage(true, 'Failed to start import. Please try again.', 'error', 5000));
    return null;
  }
};

/**
 * Starts the import pipeline after file upload.
 * @param {string} importId
 */
export const startImport = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    await axios(
      await createSignedRequest('POST', apiURL + BASE + `/${importId}/start`, JSON.stringify({}), orgHeader(state)),
    );
    dispatch(setPolling(true));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to start import'));
    dispatch(toggleAlertMessage(true, 'Failed to start import pipeline.', 'error', 5000));
  }
};

/**
 * Fetches current import job status (used for polling).
 * @param {string} importId
 */
export const getImportStatus = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}`, '', orgHeader(state)),
    );
    const job = normalizeKeys(response.data);
    dispatch(setCurrentJob(job));
    return job;
  } catch (e) {
    dispatch(setError(e.message || 'Failed to fetch import status'));
    return null;
  }
};

/**
 * Resumes import after user classification.
 * @param {string} importId
 * @param {string} [effectiveSourceKind]
 */
export const continueImport = (importId, effectiveSourceKind) => async (dispatch, getState) => {
  const state = getState();
  try {
    const body = effectiveSourceKind ? JSON.stringify({ effectiveSourceKind }) : JSON.stringify({});
    await axios(
      await createSignedRequest('POST', apiURL + BASE + `/${importId}/continue`, body, orgHeader(state)),
    );
    dispatch(setPolling(true));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to continue import'));
  }
};

/**
 * Sets classification for an ambiguous file.
 * @param {string} importId
 * @param {string} effectiveSourceKind - BANK | LEDGER | SUPPLIER
 */
export const setClassification = (importId, effectiveSourceKind) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('classification', true));
  try {
    await axios(
      await createSignedRequest(
        'PATCH',
        apiURL + BASE + `/${importId}/classification`,
        JSON.stringify({ effectiveSourceKind }),
        orgHeader(state),
      ),
    );
    dispatch(setLoading('classification', false));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to set classification'));
    dispatch(setLoading('classification', false));
  }
};

/**
 * Corrects the date format for an import.
 * @param {string} importId
 * @param {string} dateFormat - date-fns pattern e.g. 'MM/dd/yyyy'
 */
export const setDateFormat = (importId, dateFormat) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('dateFormat', true));
  try {
    await axios(
      await createSignedRequest(
        'PATCH',
        apiURL + BASE + `/${importId}/date-format`,
        JSON.stringify({ dateFormat }),
        orgHeader(state),
      ),
    );
    dispatch(setLoading('dateFormat', false));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to update date format'));
    dispatch(setLoading('dateFormat', false));
  }
};

/**
 * Loads staging rows for an import.
 * @param {string} importId
 * @param {{ silent?: boolean }} [options] - When `silent` is true, skips global loading flags (use after inline actions so the UI does not blank).
 */
export const getStagingItems = (importId, options = {}) => async (dispatch, getState) => {
  const silent = Boolean(options.silent);
  const state = getState();
  if (!silent) dispatch(setLoading('staging', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}/staging`, '', orgHeader(state)),
    );
    const normalized = normalizeKeys(response.data);
    const rows = Array.isArray(normalized) ? normalized : normalized?.rows || normalized?.items || normalized?.stagingItems || [];
    dispatch(setStaging(rows));
    if (!silent) dispatch(setLoading('staging', false));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to load staging rows'));
    if (!silent) dispatch(setLoading('staging', false));
  }
};

/**
 * Loads bank-blocklist-filtered rows for audit.
 * @param {string} importId
 */
export const getFilteredRows = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}/filtered-rows`, '', orgHeader(state)),
    );
    const normalized = normalizeKeys(response.data);
    const rows = Array.isArray(normalized) ? normalized : normalized?.rows || normalized?.items || [];
    dispatch(setFilteredRows(rows));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to load filtered rows'));
  }
};

/**
 * Loads factor match results for the review screen.
 * @param {string} importId
 * @param {{ silent?: boolean }} [options] - When `silent` is true, skips global loading flags (use after confirm/reject so the list stays mounted).
 * @returns {Promise<Array|null>} Flattened matches (one entry per suggestion), or null on failure.
 */
export const getFactorMatches = (importId, options = {}) => async (dispatch, getState) => {
  const silent = Boolean(options.silent);
  const state = getState();
  if (!silent) dispatch(setLoading('matches', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + `/my/intelligent-imports/${importId}/matches`, '', orgHeader(state)),
    );
    const normalized = normalizeKeys(response.data);
    const matchRows = Array.isArray(normalized)
      ? normalized
      : normalized?.matches || normalized?.rows || normalized?.items || [];

    const flattened = matchRows.flatMap((match) => {
      const confirmedFactorId = match.factorId || match.factor_id;
      const confirmedFactorName = match.factorName || match.factor_name;
      const isConfirmed = match.status === 'CONFIRMED';

      let suggestions = match.suggestions;
      if (typeof suggestions === 'string') {
        try { suggestions = JSON.parse(suggestions); } catch { suggestions = []; }
      }
      if (!Array.isArray(suggestions) || suggestions.length === 0) {
        return [{
          ...match,
          factorId: confirmedFactorId,
          factorName: confirmedFactorName || 'Unknown factor',
          confidence: parseFloat(match.confidence) || 0,
          isTopMatch: true,
        }];
      }

      const mapped = suggestions.map((s) => {
        const norm = normalizeKeys(s);
        const fId = norm.factorId || s.factor_id;
        const rawEntities = norm.entities || s.entities;
        const entities = Array.isArray(rawEntities) ? rawEntities.map((e) => normalizeKeys(e)) : [];
        const sugFd = norm.factorDimensions || s.factor_dimensions;
        const matchFd = match.factorDimensions || match.factor_dimensions || {};
        const mergedFactorDimensions =
          sugFd && typeof sugFd === 'object' && Object.keys(sugFd).length > 0
            ? { ...matchFd, ...sugFd }
            : norm.factorDimensions || s.factor_dimensions || match.factorDimensions || match.factor_dimensions;
        return {
          ...match,
          factorId: fId,
          factorName:
            norm.factorName ||
            norm.factorDisplayName ||
            s.factor_name ||
            s.factor_display_name,
          confidence: parseFloat(norm.confidence || s.confidence) || 0,
          co2ePerUnit: parseFloat(norm.co2ePerUnit || s.co2e_per_unit) || 0,
          subentity: norm.subentity || s.subentity,
          metric: norm.metric || s.metric,
          factorDimensions: mergedFactorDimensions,
          unitFamily: norm.unitFamily || norm.unit_family || sugFd?.unit_family || mergedFactorDimensions?.unit_family,
          unit_family: norm.unit_family || norm.unitFamily || sugFd?.unit_family || mergedFactorDimensions?.unit_family,
          qualityTier: norm.qualityTier || norm.quality_tier,
          quality_tier: norm.quality_tier || norm.qualityTier,
          detectedAmount: norm.detectedAmount ?? norm.detected_amount ?? match.detectedAmount ?? match.detected_amount,
          detected_amount: norm.detected_amount ?? norm.detectedAmount ?? match.detected_amount ?? match.detectedAmount,
          detectedUnit: norm.detectedUnit ?? norm.detected_unit ?? match.detectedUnit ?? match.detected_unit,
          detected_unit: norm.detected_unit ?? norm.detectedUnit ?? match.detected_unit ?? match.detectedUnit,
          entities,
          isTopMatch: false,
        };
      });

      if (isConfirmed && confirmedFactorId) {
        const confirmedInSuggestions = mapped.find((m) => String(m.factorId) === String(confirmedFactorId));
        if (confirmedInSuggestions) {
          confirmedInSuggestions.isTopMatch = true;
          confirmedInSuggestions.confidence = parseFloat(match.confidence) || confirmedInSuggestions.confidence;
          const idx = mapped.indexOf(confirmedInSuggestions);
          if (idx > 0) { mapped.splice(idx, 1); mapped.unshift(confirmedInSuggestions); }
        } else {
          mapped.unshift({
            rowId: match.rowId,
            importId: match.importId,
            status: match.status,
            createdAt: match.createdAt,
            factorId: confirmedFactorId,
            factorName: confirmedFactorName || 'Unknown factor',
            confidence: parseFloat(match.confidence) || 0,
            isTopMatch: true,
          });
        }
      } else {
        if (mapped.length > 0) mapped[0].isTopMatch = true;
      }

      return mapped;
    });
    dispatch(setMatches(flattened));
    if (!silent) dispatch(setLoading('matches', false));
    return flattened;
  } catch (e) {
    dispatch(setError(e.message || 'Failed to load factor matches'));
    if (!silent) dispatch(setLoading('matches', false));
    return null;
  }
};

/**
 * Confirms a factor match for a staging row.
 * Backend uses :rowId (staging row ID) as the match identifier.
 * @param {string} importId
 * @param {string} rowId - staging row ID
 * @param {string} factorId
 * @param {string} [factorName]
 */
export const confirmMatch = (importId, rowId, factorId, factorName, entityId, extra) => async (dispatch, getState) => {
  const state = getState();
  try {
    const body = { factor_id: factorId };
    if (factorName) body.factor_name = factorName;
    if (entityId) body.entity_id = entityId;
    if (extra?.activityAmount) body.activity_amount = extra.activityAmount;
    if (extra?.activityUnit) body.activity_unit = extra.activityUnit;
    if (extra?.qualityTier) body.quality_tier = extra.qualityTier;
    if (extra?.usageDateStart) body.usage_date_start = extra.usageDateStart;
    if (extra?.usageDateEnd) body.usage_date_end = extra.usageDateEnd;
    if (extra?.periodStart) body.period_start = extra.periodStart;
    if (extra?.periodEnd) body.period_end = extra.periodEnd;
    if (extra?.facilityId) body.facility_id = extra.facilityId;

    const response = await axios(
      await createSignedRequest(
        'POST',
        apiURL + `/my/intelligent-imports/${importId}/matches/${rowId}/confirm`,
        JSON.stringify(body),
        orgHeader(state),
      ),
    );
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(setError(e.message || 'Failed to confirm match'));
    return null;
  }
};

/**
 * Reverts a confirmed match back to review (PENDING_REVIEW) so the user can change factor or amounts.
 * Backend: POST /my/intelligent-imports/:importId/matches/:rowId/unconfirm
 * @param {string} importId
 * @param {string} rowId - staging row ID
 */
export const unconfirmMatch = (importId, rowId) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        apiURL + `/my/intelligent-imports/${importId}/matches/${rowId}/unconfirm`,
        JSON.stringify({}),
        orgHeader(state),
      ),
    );
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(setError(e.message || 'Failed to unconfirm match'));
    dispatch(toggleAlertMessage(true, e?.response?.data?.message || 'Could not unconfirm match. The API may not be available yet.', 'error', 6000));
    return null;
  }
};

/**
 * Rejects a factor match.
 * @param {string} importId
 * @param {string} rowId - staging row ID
 * @param {string} [factorId]
 * @param {string} [factorName]
 * @param {string} [reason] - e.g. 'wrong_category', 'wrong_unit', 'not_relevant'
 */
export const rejectMatch = (importId, rowId, factorId, factorName, reason) => async (dispatch, getState) => {
  const state = getState();
  try {
    const body = {};
    if (factorId) body.factor_id = factorId;
    if (factorName) body.factor_name = factorName;
    if (reason) body.reason = reason;

    const response = await axios(
      await createSignedRequest(
        'POST',
        apiURL + `/my/intelligent-imports/${importId}/matches/${rowId}/reject`,
        JSON.stringify(body),
        orgHeader(state),
      ),
    );
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(setError(e.message || 'Failed to reject match'));
    return null;
  }
};

/**
 * Bulk-confirms all matches at or above a confidence threshold.
 * @param {string} importId
 * @param {number} [minConfidence=0.85]
 */
export const bulkConfirmHighConfidence = (importId, minConfidence = 0.85) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('bulkConfirm', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        apiURL + `/my/intelligent-imports/${importId}/matches/bulk-confirm`,
        JSON.stringify({ minConfidence }),
        orgHeader(state),
      ),
    );
    dispatch(setLoading('bulkConfirm', false));
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(setError(e.message || 'Failed to bulk confirm'));
    dispatch(setLoading('bulkConfirm', false));
    return null;
  }
};

/**
 * Overrides a false-positive duplicate skip. The row moves to DEDUP_OVERRIDDEN, dedup keys are cleared,
 * and the API may start the matching Step Function (same as POST retry-match). When the response includes
 * `match_triggered: true`, polling is enabled — do not call `retryMatch` separately.
 * @param {string} importId
 * @param {string} stagingId
 * @returns {Promise<{ success: true, matchTriggered: boolean, row: object } | false>}
 */
export const overrideDedupSkip = (importId, stagingId) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest(
        'PATCH',
        apiURL + BASE + `/${importId}/staging/${stagingId}`,
        JSON.stringify({ dedupOverride: true }),
        orgHeader(state),
      ),
    );
    const normalized = normalizeKeys(response.data || {});
    await dispatch(getImportStatus(importId));
    if (normalized.matchTriggered) {
      dispatch(setPolling(true));
    }
    dispatch(toggleAlertMessage(true, 'Duplicate skip removed — row will be re-processed.', 'success', 5000));
    return { success: true, matchTriggered: Boolean(normalized.matchTriggered), row: normalized };
  } catch (e) {
    dispatch(setError(e.message || 'Failed to override dedup'));
    dispatch(toggleAlertMessage(true, e.message || 'Could not override duplicate skip.', 'error', 5000));
    return false;
  }
};

/**
 * Loads enrichment candidates for document-to-spend linking.
 * @param {string} importId
 */
export const getEnrichmentCandidates = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}/enrichment-candidates`, '', orgHeader(state)),
    );
    const normalized = normalizeKeys(response.data);
    dispatch(setEnrichment(Array.isArray(normalized) ? normalized : normalized?.rows || normalized?.items || []));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to load enrichment candidates'));
  }
};

/**
 * Links a document row to an existing spend row.
 * @param {string} importId
 * @param {string} documentStagingId
 * @param {string} spendStagingId
 */
export const linkEnrichment = (importId, documentStagingId, spendStagingId) => async (dispatch, getState) => {
  const state = getState();
  try {
    await axios(
      await createSignedRequest(
        'POST',
        apiURL + BASE + `/${importId}/enrichment-link`,
        JSON.stringify({ documentStagingId, spendStagingId }),
        orgHeader(state),
      ),
    );
  } catch (e) {
    dispatch(setError(e.message || 'Failed to link enrichment'));
  }
};

/**
 * Commits all confirmed matches to usageData.
 * @param {string} importId
 */
export const commitImport = (importId) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('commit', true));
  try {
    await axios(
      await createSignedRequest('POST', apiURL + BASE + `/${importId}/commit`, JSON.stringify({}), orgHeader(state)),
    );
    const statusResponse = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}`, '', orgHeader(state)),
    );
    const job = normalizeKeys(statusResponse.data);
    dispatch(setCurrentJob(job));
    dispatch(setLoading('commit', false));
    dispatch(toggleAlertMessage(true, 'Import committed successfully.', 'success', 5000));
    return job;
  } catch (e) {
    dispatch(setError(e.message || 'Failed to commit import'));
    dispatch(setLoading('commit', false));
    dispatch(toggleAlertMessage(true, 'Failed to commit import data.', 'error', 5000));
    return null;
  }
};

/**
 * Cancels an import job.
 * @param {string} importId
 */
export const cancelImport = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    await axios(
      await createSignedRequest('POST', apiURL + BASE + `/${importId}/cancel`, JSON.stringify({}), orgHeader(state)),
    );
    dispatch(setCurrentJob({ pipelineStatus: 'CANCELLED' }));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to cancel import'));
  }
};

/**
 * Splits a staging row into multiple sub-rows.
 * @param {string} importId
 * @param {string} stagingId
 * @param {Array<{description: string, amount: number, date?: string, notes?: string}>} subRows
 */
export const splitStagingRow = (importId, stagingId, subRows) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('split', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        apiURL + BASE + `/${importId}/staging/${stagingId}/split`,
        JSON.stringify({ subRows }),
        orgHeader(state),
      ),
    );
    dispatch(setLoading('split', false));
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(setError(e.message || 'Failed to split row'));
    dispatch(setLoading('split', false));
    dispatch(toggleAlertMessage(true, 'Failed to split row. Sub-row amounts must equal the original.', 'error', 5000));
    return null;
  }
};

/**
 * Excludes a staging row from the import.
 * Sets pipelineStatus=EXCLUDED, writes audit event, optionally creates an exclusion rule.
 * @param {string} importId
 * @param {string} stagingId
 * @param {{ reason: string, notes?: string, remember?: boolean }} payload
 */
export const excludeRow = (importId, stagingId, payload) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('exclude', true));
  try {
    const response = await axios(
      await createSignedRequest(
        'POST',
        apiURL + BASE + `/${importId}/staging/${stagingId}/exclude`,
        JSON.stringify(payload),
        orgHeader(state),
      ),
    );
    dispatch(setLoading('exclude', false));
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(setError(e.message || 'Failed to exclude row'));
    dispatch(setLoading('exclude', false));
    dispatch(toggleAlertMessage(true, e?.response?.data?.message || 'Failed to exclude row', 'error', 5000));
    return null;
  }
};

/**
 * Searches for emission factors by keyword.
 * @param {string} query
 * @param {object} [options]
 * @param {string} [options.scope]
 * @param {string} [options.category]
 * @param {number} [options.limit]
 * @param {boolean} [options.includeCustom]
 */
export const searchFactors = (query, options = {}) => async (dispatch, getState) => {
  const state = getState();
  try {
    const qs = new URLSearchParams({ q: query });
    if (options.scope) qs.set('scope', options.scope);
    if (options.category) qs.set('category', options.category);
    if (options.limit) qs.set('limit', String(options.limit));
    if (options.includeCustom === false) qs.set('includeCustom', 'false');

    const response = await axios(
      await createSignedRequest('GET', apiURL + `/my/factors/search?${qs}`, '', orgHeader(state)),
    );
    const normalized = normalizeKeys(response.data);
    return Array.isArray(normalized) ? normalized : normalized?.rows || normalized?.items || normalized?.results || [];
  } catch (e) {
    return [];
  }
};

/**
 * Records an audit event for an import.
 * @param {string} importId
 * @param {string} eventType
 * @param {string} [stagingId]
 * @param {object} [data]
 */
export const recordAuditEvent = (importId, eventType, stagingId, data) => async (dispatch, getState) => {
  const state = getState();
  try {
    const body = { eventType };
    if (stagingId) body.stagingId = stagingId;
    if (data) body.data = data;

    await axios(
      await createSignedRequest(
        'POST',
        apiURL + BASE + `/${importId}/audit-events`,
        JSON.stringify(body),
        orgHeader(state),
      ),
    );
  } catch (e) {
    // non-critical, don't block user flow
  }
};

/**
 * Fetches audit events for an import.
 * @param {string} importId
 * @param {object} [params]
 * @param {string} [params.eventType]
 * @param {number} [params.limit]
 */
export const getAuditEvents = (importId, params = {}) => async (dispatch, getState) => {
  const state = getState();
  try {
    const qs = new URLSearchParams();
    if (params.eventType) qs.set('eventType', params.eventType);
    if (params.limit) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';

    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}/audit-events${query}`, '', orgHeader(state)),
    );
    const normalized = normalizeKeys(response.data);
    return Array.isArray(normalized) ? normalized : normalized?.rows || normalized?.items || normalized?.events || [];
  } catch (e) {
    return [];
  }
};

/**
 * Re-runs factor matching from staging (skip parse).
 * @param {string} importId
 */
export const retryMatch = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    await axios(
      await createSignedRequest('POST', apiURL + BASE + `/${importId}/retry-match`, JSON.stringify({}), orgHeader(state)),
    );
    dispatch(setPolling(true));
    await dispatch(getImportStatus(importId));
  } catch (e) {
    dispatch(setError(e.message || 'Failed to retry matching'));
  }
};

/**
 * Lists past import jobs for the current org.
 * Falls back gracefully if the endpoint doesn't exist yet (P0 backend gap).
 */
export const getImportHistory = () => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('history', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE, '', orgHeader(state)),
    );
    const normalizedHistory = normalizeKeys(response.data);
    const jobs = Array.isArray(normalizedHistory) ? normalizedHistory : normalizedHistory?.rows || normalizedHistory?.items || [];
    dispatch(setJobs(jobs));
    dispatch(setLoading('history', false));
  } catch (e) {
    dispatch(setJobs([]));
    dispatch(setLoading('history', false));
  }
};

/**
 * Permanently deletes a single import and all associated data (admin).
 * @param {string} importId
 * @returns {Promise<boolean>}
 */
export const deleteImport = (importId) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('deleteImport', true));
  try {
    await axios(
      await createSignedRequest('DELETE', apiURL + BASE + `/${importId}`, '', orgHeader(state)),
    );
    const currentId = getState().intelligentImport?.currentJob?.id;
    if (currentId != null && String(currentId) === String(importId)) {
      dispatch(setPolling(false));
      dispatch(clearJob());
    }
    await dispatch(getImportHistory());
    dispatch(setLoading('deleteImport', false));
    dispatch(toggleAlertMessage(true, 'Import deleted permanently.', 'success', 5000));
    return true;
  } catch (e) {
    dispatch(setError(e.message || 'Failed to delete import'));
    dispatch(setLoading('deleteImport', false));
    dispatch(
      toggleAlertMessage(true, e?.response?.data?.message || e.message || 'Could not delete import.', 'error', 5000),
    );
    return false;
  }
};

/**
 * Deletes every import for the current organisation (admin / test cleanup).
 * @returns {Promise<boolean>}
 */
export const purgeAllImports = () => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('purgeImports', true));
  try {
    await axios(await createSignedRequest('DELETE', apiURL + `${BASE}?confirm=true`, '', orgHeader(state)));
    dispatch(setPolling(false));
    dispatch(clearJob());
    dispatch(setJobs([]));
    dispatch(setLoading('purgeImports', false));
    dispatch(toggleAlertMessage(true, 'All imports for this organisation were deleted.', 'success', 5000));
    return true;
  } catch (e) {
    dispatch(setError(e.message || 'Failed to purge imports'));
    dispatch(setLoading('purgeImports', false));
    dispatch(
      toggleAlertMessage(true, e?.response?.data?.message || e.message || 'Could not purge imports.', 'error', 5000),
    );
    return false;
  }
};

/**
 * Loads the org's default date format and supported formats.
 */
export const getOrgDateFormat = () => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + '/my/org/date-format', '', orgHeader(state)),
    );
    dispatch(setOrgDateFormat(normalizeKeys(response.data)));
  } catch (e) {
    // non-critical
  }
};

/**
 * Updates the org's default date format.
 * @param {string} defaultDateFormat - date-fns pattern
 */
export const setOrgDefaultDateFormat = (defaultDateFormat) => async (dispatch, getState) => {
  const state = getState();
  try {
    await axios(
      await createSignedRequest(
        'PUT',
        apiURL + '/my/org/date-format',
        JSON.stringify({ defaultDateFormat }),
        orgHeader(state),
      ),
    );
    dispatch(setOrgDateFormat({ defaultDateFormat }));
    dispatch(toggleAlertMessage(true, 'Date format updated.', 'success', 3000));
  } catch (e) {
    dispatch(toggleAlertMessage(true, 'Failed to update date format.', 'error', 5000));
  }
};

/**
 * Fetches the AI usage quota for the org.
 */
export const getAiQuota = () => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + '/my/org/quota', '', orgHeader(state)),
    );
    dispatch(setAiQuota(normalizeKeys(response.data)));
  } catch (e) {
    // non-critical
  }
};

/**
 * Fetches the org's quality score.
 * @param {object} [params]
 * @param {boolean} [params.breakdown] - include per-category/scope breakdown
 * @param {string} [params.reportingYear]
 */
export const getQualityScore = (params = {}) => async (dispatch, getState) => {
  const state = getState();
  try {
    const qs = new URLSearchParams();
    if (params.breakdown) qs.set('breakdown', 'true');
    if (params.reportingYear) qs.set('reportingYear', params.reportingYear);
    const query = qs.toString() ? `?${qs}` : '';

    const response = await axios(
      await createSignedRequest('GET', apiURL + `/my/org/quality-score${query}`, '', orgHeader(state)),
    );
    const score = normalizeKeys(response.data);
    dispatch(setQualityScore(score));
    return score;
  } catch (e) {
    return null;
  }
};

/**
 * Loads ledger code mappings for an import.
 * @param {string} importId
 */
export const getLedgerMappings = (importId) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + BASE + `/${importId}/ledger-mappings`, '', orgHeader(state)),
    );
    const normalizedLM = normalizeKeys(response.data);
    dispatch(setLedgerMappings(Array.isArray(normalizedLM) ? normalizedLM : normalizedLM?.rows || normalizedLM?.items || []));
  } catch (e) {
    // non-critical
  }
};

/**
 * Creates or updates a ledger code mapping.
 * @param {string} code - account code
 * @param {object} mapping - { subentityFilter, dimensionFilters, factorId, label }
 */
export const updateLedgerMapping = (code, mapping) => async (dispatch, getState) => {
  const state = getState();
  try {
    await axios(
      await createSignedRequest(
        'PUT',
        apiURL + BASE + `/ledger-mappings/${encodeURIComponent(code)}`,
        JSON.stringify(mapping),
        orgHeader(state),
      ),
    );
    dispatch(toggleAlertMessage(true, 'Ledger mapping updated.', 'success', 3000));
  } catch (e) {
    dispatch(toggleAlertMessage(true, 'Failed to update ledger mapping.', 'error', 5000));
  }
};

/**
 * Creates a custom emission factor for the org.
 * @param {object} factorData - { name, emissionFactor, unit, supplierName?, scope?, category?, validFrom?, validTo? }
 */
export const createCustomFactor = (factorData) => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('POST', apiURL + '/my/factors/custom', JSON.stringify(factorData), orgHeader(state)),
    );
    dispatch(toggleAlertMessage(true, 'Custom factor created.', 'success', 3000));
    return normalizeKeys(response.data);
  } catch (e) {
    dispatch(toggleAlertMessage(true, 'Failed to create custom factor.', 'error', 5000));
    return null;
  }
};

/**
 * Lists the org's custom emission factors.
 */
export const getCustomFactors = () => async (dispatch, getState) => {
  const state = getState();
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + '/my/factors/custom', '', orgHeader(state)),
    );
    const normalizedCF = normalizeKeys(response.data);
    return Array.isArray(normalizedCF) ? normalizedCF : normalizedCF?.rows || normalizedCF?.items || [];
  } catch (e) {
    return [];
  }
};

/**
 * Fetches pending-review matches for an import.
 * @param {string} importId
 */
export const getPendingMatches = (importId) => async (dispatch, getState) => {
  const state = getState();
  dispatch(setLoading('pendingMatches', true));
  try {
    const response = await axios(
      await createSignedRequest('GET', apiURL + `/my/intelligent-imports/${importId}/matches/pending`, '', orgHeader(state)),
    );
    dispatch(setLoading('pendingMatches', false));
    const normalizedPM = normalizeKeys(response.data);
    return Array.isArray(normalizedPM)
      ? normalizedPM
      : normalizedPM?.matches || normalizedPM?.rows || normalizedPM?.items || [];
  } catch (e) {
    dispatch(setLoading('pendingMatches', false));
    return [];
  }
};

export const Actions = {
  Types,
  initiateImport,
  startImport,
  getImportStatus,
  continueImport,
  setClassification,
  setDateFormat,
  getStagingItems,
  getFilteredRows,
  getFactorMatches,
  getPendingMatches,
  confirmMatch,
  unconfirmMatch,
  rejectMatch,
  bulkConfirmHighConfidence,
  overrideDedupSkip,
  splitStagingRow,
  excludeRow,
  searchFactors,
  recordAuditEvent,
  getAuditEvents,
  getEnrichmentCandidates,
  linkEnrichment,
  commitImport,
  cancelImport,
  retryMatch,
  getImportHistory,
  deleteImport,
  purgeAllImports,
  getOrgDateFormat,
  setOrgDefaultDateFormat,
  getAiQuota,
  getQualityScore,
  getLedgerMappings,
  updateLedgerMapping,
  createCustomFactor,
  getCustomFactors,
  clearJob,
  setPolling,
};
